import React, { useEffect, useState } from "react";
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
  Alert,
  AlertTitle,
  AlertDescription,
  TrafficBadge,
  Link,
  cn,
  Heading,
} from "@ogt/ui";

import { buildSpaUrl } from "src/config/urls";

interface LineTypeInfo {
  DefiningAttribute: string;
  Id: number;
  Key: string;
  Label: string;
  OgtTrafficTypeGroupCssClass: string;
  OgtTrafficTypeIconCssClass: string;
  OgtTrafficTypeIconReplaceLineName: boolean;
}

interface LineInfo {
  LineNrReal: number;
  LineName: string;
  Heading: string;
  LineType: LineTypeInfo;
}

interface LinesPassingStopProps {
  stopId: number | null;
}

type TrafficMeta = Parameters<typeof TrafficBadge>[0]["meta"];

function toTrafficMetaFromLine(line: LineInfo): TrafficMeta {
  return {
    iconCss: line.LineType.OgtTrafficTypeIconCssClass,
    groupCss: line.LineType.OgtTrafficTypeGroupCssClass,
    title: line.LineName ?? "",
  };
}

export const LinesPassingStop = ({ stopId }: LinesPassingStopProps) => {
  const [, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lines, setLines] = useState<LineInfo[]>([]);

  const apiBaseUrl =
    process.env.REACT_APP_TPS_API_BASE_URL?.replace(/\/$/, "") ?? "";

  useEffect(() => {
    if (!stopId) return;

    setLoading(true);
    setError(null);

    fetch(`${apiBaseUrl}/api/v1/StopAreas/LinesPassingStop/?stopId=${stopId}`)
      .then((response) => {
        if (!response.ok) throw new Error("Failed to fetch");
        return response.json();
      })
      .then((data) => {
        setLines(data || []);
        setLoading(false);
      })
      .catch((err) => {
        console.error("Details fetch error:", err);
        setError("Could not load lines");
        setLoading(false);
      });
  }, [stopId, apiBaseUrl]);

  if (!stopId) return null;
  if (error)
    return (
      <Alert variant="error">
        <AlertTitle>Error:</AlertTitle>
        <AlertDescription>{error}</AlertDescription>
      </Alert>
    );

  return (
    <>
      <Heading as="h4">Linjer som passerar hållplatsen</Heading>

      <Accordion type="single">
        <AccordionItem value="lines-accordion-1">
          <AccordionTrigger>
            Visa alla ({lines.length}) {lines.length === 1 ? "linje" : "linjer"}
          </AccordionTrigger>
          <AccordionContent>
            {
              <>
                {lines.length > 0 && (
                  <>
                    <ul className="list-none">
                      {lines.map((line, index) => {
                        const isPendeln =
                          line.LineName?.includes("Östgötapendeln");
                        const showText =
                          !line.LineType.OgtTrafficTypeIconReplaceLineName;
                        const mapLineId = String(line.LineNrReal);

                        const href = buildSpaUrl("map", {
                          linje: mapLineId,
                          aktivLinje: mapLineId,
                        });

                        return (
                          <li
                            className="flex items-center mb-0 py-2"
                            key={index}
                          >
                            <Link
                              href={href}
                              icon="chevron-right"
                              iconPosition="right"
                              size="base"
                              underline="none"
                              variant="default"
                              className="group !no-underline hover:!no-underline active:!no-underline focus-visible:!no-underline"
                            >
                              <TrafficBadge
                                meta={toTrafficMetaFromLine(line)}
                                className={cn(
                                  line.LineType.OgtTrafficTypeGroupCssClass,
                                  isPendeln && "pendeln",
                                  "!no-underline"
                                )}
                                titleOverride={line.LineName}
                              >
                                {showText ? line.LineName : null}
                              </TrafficBadge>

                              <span className="ml-2 underline group-hover:no-underline group-active:no-underline group-focus-visible:no-underline">
                                {!line.LineType
                                  .OgtTrafficTypeIconReplaceLineName &&
                                  "Linje "}
                                {line.LineName}
                              </span>
                            </Link>
                          </li>
                        );
                      })}
                    </ul>
                  </>
                )}
              </>
            }
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </>
  );
};
